/**
 * Environment Provider
 * Handles environment completions inside \begin{...} and \end{...}
 */

import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { EditorView } from '@codemirror/view'
import { PositionInfo, LaTeXContext, EnvironmentCategory } from '../types'
import { CompletionProvider } from './baseProvider'
import { findEnvironments } from '@/utils/latexDataLoader'
import { logDebug } from '@utils/logger'

export class EnvironmentProvider implements CompletionProvider {
  name = 'environment'
  priority = 90 // High priority, but lower than command provider

  canHandle(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): boolean {
    return position.isInEnvironment || 
           (position.isInCommand && (position.commandName === 'begin' || position.commandName === 'end'))
  }

  provide(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): CompletionResult | null {
    const { state, pos } = context
    const text = state.doc.toString()
    
    // Find the \begin{ or \end{ position
    let beginPos = -1
    let envStart = pos
    let isEnd = false
    
    // Scan backwards to find \begin{ or \end{
    for (let i = pos - 1; i >= 0 && (pos - i) <= 100; i--) {
      if (text.substring(i - 6, i + 1) === '\\begin{') {
        beginPos = i - 6
        envStart = i + 1
        break
      } else if (text.substring(i - 4, i + 1) === '\\end{') {
        beginPos = i - 4
        envStart = i + 1
        isEnd = true
        break
      }
    }
    
    if (beginPos < 0) return null
    
    // Get current query (text after {)
    const query = text.substring(envStart, pos).toLowerCase()
    
    // Get environment matches
    let envMatches = findEnvironments(query)
    
    // Filter by context if in math mode (prefer math environments)
    if (latexContext.inMathMode) {
      const mathEnvs = ['equation', 'equation*', 'align', 'align*', 'gather', 'gather*',
                       'multline', 'multline*', 'split', 'array', 'eqnarray', 'eqnarray*']
      const mathMatches = envMatches.filter(env => mathEnvs.includes(env.toLowerCase()))
      const otherMatches = envMatches.filter(env => !mathEnvs.includes(env.toLowerCase()))
      envMatches = [...mathMatches, ...otherMatches]
    }
    
    if (envMatches.length === 0) return null
    
    const capturedBeginPos = beginPos
    const capturedPos = pos
    const capturedIsEnd = isEnd
    
    const options = envMatches.slice(0, 30).map(envName => {
      if (capturedIsEnd) {
        // For \end{}, just insert the environment name
        return {
          label: envName,
          type: 'keyword' as const,
          info: `End ${envName} environment`,
          apply: envName
        }
      } else {
        // For \begin{}, insert full environment with cursor inside
        const beginTag = `\\begin{${envName}}`
        const endTag = `\\end{${envName}}`
        
        // Smart insertion: put cursor inside environment with proper indentation
        const insertText = `${beginTag}\n\t$0\n${endTag}`
        const cursorPos = capturedBeginPos + beginTag.length + 2 // After \n\t
        
        return {
          label: envName,
          type: 'keyword' as const,
          info: `LaTeX environment: ${envName}`,
          apply: (view: EditorView) => {
            // Replace from \begin{ to current position
            view.dispatch({
              changes: {
                from: capturedBeginPos,
                to: capturedPos,
                insert: insertText
              },
              selection: {
                anchor: cursorPos
              }
            })
          }
        }
      }
    })
    
    logDebug('[EnvironmentProvider] Providing completions', {
      query,
      optionsCount: options.length,
      isEnd: capturedIsEnd
    })
    
    return {
      from: envStart,
      to: pos,
      options,
      filter: false,
      span: /^[a-zA-Z*]*$/,
      validFor: /^[a-zA-Z*]*$/
    } as CompletionResult & { span: RegExp; validFor: RegExp }
  }
}

